import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useToast } from './use-toast';
import { commitContactImport } from '../../supabase/functions/_shared/contactImport';
import type { UploadRequest } from '../../supabase/functions/_shared/marketingConsent';
import { saveManagedClientConsent } from '@/lib/campaignContactList';

function describeLeadWriteError(error: { message?: string } | null | undefined, fallback: string): string {
  const message = error?.message ?? "";
  if (/contact|consent|third-party|sender|lawful|list/i.test(message)) return message;
  return fallback;
}

export interface Lead {
  id: string;
  campaign_id?: string;
  user_id: string;
  company_name: string;
  contact_name?: string;
  phone: string;
  email?: string;
  industry?: string;
  company_size?: string;
  clay_enrichment_data?: any;
  call_status: 'pending' | 'called' | 'completed' | 'failed';
  last_call_attempt?: string;
  created_at?: string;
  updated_at?: string;
  notes?: string;
}

export const useLeads = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchLeads();
    }
  }, [user]);

  const fetchLeads = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('leads')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setLeads((data || []) as Lead[]);
    } catch (error: any) {
      console.error('Error fetching leads:', error);
      toast({
        title: "Error",
        description: "Failed to fetch leads",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const createLead = async (_leadData: Omit<Lead, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => {
    const message = "Add this contact with a phone consent record. A lead cannot be created without one.";
    toast({ title: "Consent required", description: message, variant: "destructive" });
    throw new Error(message);
  };

  const updateLead = async (id: string, updates: Partial<Lead>) => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('leads')
        .update(updates)
      .eq('id', id)
      .eq('user_id', user.id)
      .select()
      .single();

  if(error) throw error;

setLeads(prev => prev.map(lead =>
  lead.id === id ? data as Lead : lead
));

toast({
  title: "Success",
  description: "Lead updated successfully",
});

return data;
  } catch (error: any) {
  console.error('Error updating lead:', error);
  toast({
    title: "Error",
    description: describeLeadWriteError(error, "Failed to update lead"),
    variant: "destructive",
  });
  throw error;
}
};

const deleteLead = async (id: string) => {
  if (!user) return;

  try {
    const { error } = await supabase
      .from('leads')
      .delete()
      .eq('id', id)
      .eq('user_id', user.id);

    if (error) throw error;

    setLeads(prev => prev.filter(lead => lead.id !== id));
    toast({
      title: "Success",
      description: "Lead deleted successfully",
    });
  } catch (error: any) {
    console.error('Error deleting lead:', error);
    toast({
      title: "Error",
      description: "Failed to delete lead",
      variant: "destructive",
    });
    throw error;
  }
};

const importLeads = async (request: UploadRequest) => {
  if (!user) return;

  try {
    const result = await commitContactImport(supabase as unknown as Parameters<typeof commitContactImport>[0], {
      ...request,
      accountUserId: request.accountUserId || user.id,
      actorUserId: request.actorUserId || user.id,
    }, new Date(), {
      insertClientConsent: request.managedCampaign ? saveManagedClientConsent : undefined,
    });
    if (result.error && result.created === 0) {
      throw new Error(result.error);
    }

    setLeads(prev => [...((result.leads || []) as Lead[]), ...prev]);
    const rejected = result.rejections.length;
    toast({
      title: rejected ? "Imported with rejections" : "Success",
      description: rejected
        ? `${result.created} imported. ${rejected} rejected: ${result.rejections[0]?.reasons[0] ?? "missing consent"}`
        : `${result.created} leads imported successfully`,
      variant: rejected ? "destructive" : "default",
    });

    return result.leads;
  } catch (error: any) {
    console.error('Error importing leads:', error);
    toast({
      title: "Import blocked",
      description: describeLeadWriteError(error, "Failed to import leads"),
      variant: "destructive",
    });
    throw error;
  }
};

return {
  leads,
  loading,
  createLead,
  updateLead,
  deleteLead,
  importLeads,
  refetch: fetchLeads,
};
};